import { useCallback, useEffect } from 'react';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import Button from 'src/app/components/Elements/Button';
import { Card } from 'src/app/components/Elements/Card';
import RadioCardItem from 'src/app/components/Elements/Card/RadioCardItem';
import DeliveryAddress from 'src/app/components/Models/DeliveryAddress/container';
import { segmentTrack } from 'src/app/state/actions';
import { useDispatch } from 'src/app/state/store';

import styles from './styles.module.scss';

/*
 * This component is used to present the user with an option to choose between an address suggested by
 * a Pharmakon validator. It's a pure renderer, and is being used in two configurations:
 *   - a redux-connected container in the checkout flow, whose actions are handled through redux
 *   - a regular react component in the account page delivery address flow, where actions are defined
 *     in the parent component and use react-query for state management
 */
export const ValidateAddress = ({
  invalidAddress,
  suggestedAddress,
  handleEditClick,
  submitAddress,
  setSelectedAddress,
  selectedAddress,
  isFromAccountDeliveryAddress,
}) => {
  const dispatch = useDispatch();
  const dispatchSegmentTrack = useCallback(
    (event: UnifiedEvent, payload: any) => dispatch(segmentTrack(event, payload) as any),
    [dispatch]
  );

  useEffect(() => {
    dispatchSegmentTrack(UNIFIED.viewAddressValidation, {
      source: isFromAccountDeliveryAddress ? 'Your Account' : 'Checkout',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditClickWithAddress = address => e => {
    handleEditClick(e, address);
  };

  return (
    <div className={styles.component}>
      <p>We could not verify your address. Please choose the version you want to use below.</p>
      <br />
      <Card>
        {suggestedAddress && (
          <>
            <RadioCardItem
              onClick={() => setSelectedAddress('recommended')}
              isActive={selectedAddress === 'recommended'}
              header="Suggested Address"
            >
              <DeliveryAddress DeliveryAddress={{ attributes: { ...suggestedAddress } }} isVerifyPage={true} />
            </RadioCardItem>
          </>
        )}
        {invalidAddress && (
          <>
            <RadioCardItem
              onClick={() => setSelectedAddress('userEntered')}
              isActive={selectedAddress === 'userEntered'}
              header="You entered"
              data_cy={'delivery_address_select_user_entered'}
            >
              <p className={styles.edit} onClick={handleEditClickWithAddress(invalidAddress)}>
                Edit
              </p>
              <DeliveryAddress DeliveryAddress={{ attributes: { ...invalidAddress } }} isVerifyPage={true} />
            </RadioCardItem>
          </>
        )}
      </Card>

      <Button
        onClick={submitAddress}
        style="newCheckout"
        size="medium"
        block
        disabled={!selectedAddress}
        data-cy="submitAddress"
      >
        {(suggestedAddress && 'Use this address') || 'Continue with this address' /*eslint-disable-line */}
      </Button>
    </div>
  );
};
